import React, { useContext, useState, useEffect } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import axios from 'axios';
import {
	Typography,
	IconButton,
	Button,
	Grid,
	Card,
	CardContent,
	Fab,
	CardMedia,
	TextField,
	MenuItem,
	Tooltip
} from "@material-ui/core";
import { DropzoneArea, DropzoneDialog } from "material-ui-dropzone";

import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import PostAddIcon from '@material-ui/icons/PostAdd';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowLeftIcon from '@material-ui/icons/ArrowLeft';
import LaunchIcon from '@material-ui/icons/Launch';
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';

import { PageContext } from "../../contexts/PageContext";
import { UserContext } from "../../contexts/UserContext";
import { convertBytesToMbsOrKbs, stringToFilename } from "../../functions/GlobalHelpers"
import InfoTooltip from "../general/InfoTooltip";




const useStyles = makeStyles((theme) => ({
	card: {
		position: "relative"
	},
	preview: {
		height: 250,
		backgroundSize: "contain",
	},
	previewContainer: {
		position: "relative",
		backgroundColor: "#efefef",
		padding: 5,
		// justifyContent: "center",
		// display: "flex",
		// flexDirection: "column"
	},
	uploadWrap: {
		marginTop: 16,
		textAlign: "center",
	},
	dropzone: {
		padding: 20,
		height: 200,
		minHeight: 200,
		fontFamily: "Roboto, 'Segoe UI', 'Helvetica Neue', Arial",
		fontSize: 13,
		textTransform: "uppercase",
		letterSpacing: 1,
		"& p": {
			fontSize: 13,
		},
	},
	deleteButton: {
		position: "absolute",
		right: 10,
		top: 10,
		background: "#fff"
	},
	rotateButton: {
		position: "absolute",
		right: 10,
		bottom: 10,
		background: "#fff"
	},
	fileActionButtons: {
		position: "absolute",
		left: 0,
		bottom: 3,
		width: "100%",
		textAlign: "center",
	},
	fileActionButton: {
		background: "#fff",
		position: "relative",
		boxShadow: "1px 1px 3px rgba(0,0,0,0.3)",
		marginRight: 5,
		"&:hover": {
			background: "#fff",
		}
	},
	uploadActionButton: {
		marginTop: 5
	},
	iconDecorator: {
		position: "absolute",
		bottom: 0,
		right: 0,
		width: 15,
		height: 15,
		background: theme.palette.primary.main,
		color: "#FFF",
		borderRadius: "50%"
	}
}));

export default function FileUploadField(
	{
		filename_postfix = "",
		metafield_prop,
		type_suggestions = {},
		setMetafields,
		allowMultiple = true,
		allowImages = true,
		description = ""
	}) {
	const theme = useTheme();
	const classes = useStyles();
	const pageContext = useContext(PageContext);
	const userContext = useContext(UserContext);
	const [filename, setFilename] = useState("");
	const [fileDocumentType, setFileDocumentType] = useState("");
	const [fileDocumentSubType, setFileDocumentSubType] = useState("");
	const [saveTimeout, setSaveTimeout] = useState(null);
	const [dropzoneDialogAction, setDropzoneDialogAction] = useState("");
	const [metafield, setMetafield] = useState(null);
	const [forcePreviewRefresh, setForcePreviewRefresh] = useState(1);// just increment if needed

	useEffect(() => {
		const mergedFileDocumentType = getMergedFileDocumentType();
		saveDocumentTypeDelayed(mergedFileDocumentType);
		setFilename(mergedFileDocumentType + (filename_postfix ? "_" + filename_postfix : ""));
	}, [filename_postfix, fileDocumentType, fileDocumentSubType]);

	useEffect(() => {
		setMetafield(metafield_prop);
		if (metafield_prop.file_document_type) {
			const type = metafield_prop.file_document_type.split("__");
			if (type && type.length > 0) {
				if (Object.keys(type_suggestions).includes(type[0])) {
					setFileDocumentType(type[0]);
					if (type && type.length > 1) {
						setFileDocumentSubType(type[1]);
					}
				} else {
					setFileDocumentType("sonstige");
					setFileDocumentSubType(metafield_prop.file_document_type);
				}
			}
		}
	}, [metafield_prop])

	const getMergedFileDocumentType = () => {
		if (fileDocumentType === "sonstige")
			return stringToFilename(fileDocumentSubType).replace(/^\_+|\_+$/g, '');

		return [fileDocumentType, stringToFilename(fileDocumentSubType)].join("__").replace(/^\_+|\_+$/g, '');
	}

	const uploadFiles = (metafield, files, action = null) => {
		const formData = new FormData();
		files.forEach((file, index) => {
			formData.append("document_" + index, file);
		});

		formData.append("metafield_id", metafield.id);
		// if there's no type_suggestions set filename as fileDocumentType
		if (!Object.keys(type_suggestions).length) {
			let fileName = files[0].name;
			fileName = fileName.split('.').slice(0, -1).join('.');// remove extension
			formData.append("file_document_type", stringToFilename(fileName));
		} else
			formData.append("file_document_type", getMergedFileDocumentType());
		if (action)
			formData.append("action", action);
		const headers = userContext.getRequestAuthHeader(false);

		axios.post(
			`${process.env.REACT_APP_API_SERVICE_URL}/fileuploads`,
			formData, {
			headers: headers
		})
			.then(({ data }) => {
				refreshMetafieldFromServer(metafield);
				setForcePreviewRefresh(forcePreviewRefresh => forcePreviewRefresh + 1);
				pageContext.setInfoMessage(data.message);
			})
			.catch((err) => {
				pageContext.setErrorMessage("Die Datei konnte nicht gespeichert werden!");
			});
	};


	const saveFileMetafield = (mergedFileDocumentType) => {
		// return early if metafield is not defined yet
		if (!metafield)
			return;
		// console.log(mergedFileDocumentType);
		const options = {
			url: `${process.env.REACT_APP_API_SERVICE_URL}/metafields/${metafield.id}`,
			data: {
				id: metafield.id,
				file_document_type: mergedFileDocumentType
			},
			method: 'put',
			headers: userContext.getRequestAuthHeader()
		};
		axios(options)
			.then((res) => {
				//
			})
			.catch((err) => {
				console.log(err);
				pageContext.setErrorMessage("Das Feld konnte nicht gespeichert werden!");
			});

		setMetafields(fields => {
			const index = fields.findIndex(field => field.id === metafield.id);
			if (index > -1)
				fields[index] = { ...fields[index], file_document_type: mergedFileDocumentType };

			return [...fields];
		})
	}

	// warte einen Moment, könnte ja sein, dass der Subtype auch gleich geändert wird
	const saveDocumentTypeDelayed = (mergedFileDocumentType) => {
		if (saveTimeout)
			clearTimeout(saveTimeout);
		setSaveTimeout(setTimeout(() => saveFileMetafield(mergedFileDocumentType), 3000));
	}


	const rotateFile = (metafield) => {
		const options = {
			url: `${process.env.REACT_APP_API_SERVICE_URL}/fileuploads/${metafield.id}`,
			method: 'put',
			data: {
				action: "rotate180"
			},
			headers: userContext.getRequestAuthHeader()
		};
		axios(options)
			.then((res) => {
				refreshMetafieldFromServer(metafield);
				setForcePreviewRefresh(forcePreviewRefresh => forcePreviewRefresh + 1);
			})
			.catch((err) => {
				console.log(err);
				pageContext.setErrorMessage("Datei konnte nicht gedreht werden!");
			});
	}


	const maybeDeleteFile = (metafield) => {
		pageContext.setConfirmDialog({
			open: true,
			title: "Wirklich löschen?",
			message:
				"Bist du dir sicher, dass du die Datei " +
				filename +
				" löschen möchtest?",
			handleYes: () => {
				const options = {
					url: `${process.env.REACT_APP_API_SERVICE_URL}/fileuploads/${metafield.id}`,
					method: 'delete',
					headers: userContext.getRequestAuthHeader()
				};
				axios(options)
					.then((res) => {
						refreshMetafieldFromServer(metafield);
					})
					.catch((err) => {
						console.log(err);
						pageContext.setErrorMessage("Datei konnte nicht gelöscht werden!");
					});

				pageContext.setConfirmDialog({
					open: false,
				});
			},
			handleNo: () => {
				pageContext.setConfirmDialog({
					open: false,
				});
			},
		});
	}

	const refreshMetafieldFromServer = (metafield) => {
		const options = {
			url: `${process.env.REACT_APP_API_SERVICE_URL}/metafields/${metafield.id}`,
			data: {
				id: metafield.id
			},
			method: 'get',
			headers: userContext.getRequestAuthHeader()
		};
		axios(options)
			.then(({ data }) => {
				setMetafield(data);
				setMetafields(fields => {
					const index = fields.findIndex(field => field.id === data.id);
					if (index > -1)
						fields[index] = data

					return [...fields];
				})
			})
			.catch((err) => {
				console.log(err);
				pageContext.setErrorMessage("Das Feld konnte nicht geladen werden!");
			});
	}

	const renderFileDocumentSubTypeField = () => {
		if (!fileDocumentType || !type_suggestions[fileDocumentType] || !type_suggestions[fileDocumentType].subfield)
			return <></>;
		const subfield = type_suggestions[fileDocumentType].subfield;
		if (subfield.options) {
			return (
				<TextField
					select
					label={subfield.label}
					value={fileDocumentSubType}
					onChange={(e) => {
						setFileDocumentSubType(e.target.value);
					}}
					style={{ width: 150 }}
					size="small"
					variant="outlined"
				>
					{subfield.options.map((option) => (
						<MenuItem key={option} value={option}>
							{option}
						</MenuItem>
					))}
					{fileDocumentSubType && subfield.options.indexOf(fileDocumentSubType) === -1 &&
						<MenuItem key={fileDocumentSubType} value={fileDocumentSubType}>
							{fileDocumentSubType}
						</MenuItem>
					}
				</TextField>);
		}
		return (
			<TextField
				label={subfield.label}
				value={fileDocumentSubType}
				onChange={(e) => {
					setFileDocumentSubType(e.target.value);
				}}
				style={{ width: 150 }}
				size="small"
				variant="outlined"
			/>);
	}

	if (!metafield)
		return <></>;



	return (
		<Grid item xs={12} key={metafield.meta_key + "-wrap"}>
			<Card className={classes.card}>
				<Grid container spacing={0}>
					<Grid item xs={12} md={9}>
						<CardContent>
							<Grid container spacing={2}>
								<Grid item xs={12} >
									{Object.keys(type_suggestions).length > 0 &&
										<>
											<TextField
												select
												label="Dokument Typ"
												value={fileDocumentType}
												onChange={(e) => {
													setFileDocumentType(e.target.value);
													setFileDocumentSubType("");
												}}
												style={{ width: 200 }}
												size="small"
												variant="outlined"
											>
												{Object.keys(type_suggestions).map((type_key) => (
													<MenuItem key={type_key} value={type_key}>
														{type_suggestions[type_key].label}
													</MenuItem>
												))}
											</TextField>
											{renderFileDocumentSubTypeField()}
										</>
									}
								</Grid>
								<Grid item xs={12} >
									<Typography variant="caption">
										{description
											? description
											: fileDocumentType && type_suggestions[fileDocumentType] && type_suggestions[fileDocumentType].description
												? type_suggestions[fileDocumentType].description
												: "Du kannst Bilder oder PDF-Dateien hochladen. Diese werden automatisch verkleinert und Bilder werden in PDFs umgewandelt."
										}
									</Typography>
								</Grid>
								<Grid item xs={12} >
									{metafield.meta_value &&
										<>
											<Typography variant="caption">{filename}.pdf</Typography>
											<IconButton size="small" color="primary" download target="_blank" href={`${process.env.REACT_APP_API_SERVICE_URL}/fileuploads/${metafield.id}/${filename}.pdf?token=${userContext.accessToken}&v=${forcePreviewRefresh}`}>
												<GetAppIcon size="small" />
											</IconButton>
											<Typography variant="caption">{convertBytesToMbsOrKbs(metafield.meta_value_numeric)}</Typography>
										</>
									}
								</Grid>
							</Grid>
						</CardContent>
					</Grid>
					<Grid item xs={12} md={3} className={classes.previewContainer} align="center">
						{metafield.meta_value ?
							<>
								<CardMedia
									className={classes.preview}
									image={`${process.env.REACT_APP_API_SERVICE_URL}/fileuploads/${metafield.id}/preview/300/${filename}.png?token=${userContext.accessToken}&v=${forcePreviewRefresh}`}
								/>
								<div className={classes.fileActionButtons}>
									<Tooltip title="Datei öffnen">
										<IconButton className={classes.fileActionButton} size="small" color="primary" target="_blank" href={`${process.env.REACT_APP_API_SERVICE_URL}/fileuploads/${metafield.id}/${filename}.pdf?token=${userContext.accessToken}&v=${forcePreviewRefresh}`}>
											<LaunchIcon size="small" />
										</IconButton>
									</Tooltip>
									<Tooltip title="neue Seiten am Anfang des Dokuments einfügen">
										<IconButton className={classes.fileActionButton} size="small" color="primary" onClick={() => setDropzoneDialogAction("prepend")}>
											<PostAddIcon size="small" />
											<ArrowLeftIcon className={classes.iconDecorator} />
										</IconButton>
									</Tooltip>
									<Tooltip title="neue Seiten am Ende des Dokuments einfügen">
										<IconButton className={classes.fileActionButton} size="small" color="primary" onClick={() => setDropzoneDialogAction("append")}>
											<PostAddIcon size="small" />
											<ArrowRightIcon className={classes.iconDecorator} />
										</IconButton>
									</Tooltip>
									<Tooltip title="um 180° drehen">
										<IconButton className={classes.fileActionButton} size="small" color="primary" onClick={() => rotateFile(metafield)}>
											<RotateRightIcon size="small" />
										</IconButton>
									</Tooltip>
									<Tooltip title="Datei löschen">
										<IconButton className={classes.fileActionButton} size="small" color="primary" onClick={() => maybeDeleteFile(metafield)}>
											<DeleteIcon size="small" />
										</IconButton>
									</Tooltip>
								</div>
							</>
							:
							<>
								<DropzoneArea
									dropzoneClass={classes.dropzone}
									onDrop={(newFiles) => {
										console.log(newFiles);
										if (Array.isArray(newFiles)) uploadFiles(metafield, newFiles);
										else uploadFiles(metafield, [newFiles]);
									}}
									acceptedFiles={[
										"application/pdf",
										"image/png",
										"image/jpeg",
										"image/gif",
									]}
									filesLimit={1}
									maxFileSize={20000000}
									showPreviewsInDropzone={false}
									showPreviews={false}
									useChipsForPreview={false}
									showFileNamesInPreview={false}
									showFileNames={false}
									dropzoneText={"Datei hier ablegen oder zum Auswählen klicken."}
									getFileLimitExceedMessage={(filesLimit) => {
										if (filesLimit === 1)
											return "Bitte lade hier nur eine Datei hoch.";

										return "Bitte lade maximal " + filesLimit + " Dateien hoch.";
									}}
									getFileAddedMessage={(fileName) => {
										return "Datei " + fileName + " wird geladen.";
									}}
									getFileRemovedMessage={(fileName) => {
										return "Datei " + fileName + " entfernt.";
									}}
									getDropRejectMessage={(
										rejectedFile,
										acceptedFiles,
										maxFileSize
									) => {
										var message =
											"Die Datei " +
											rejectedFile.name +
											" kann nicht verwendet werden. ";

										if (!acceptedFiles.includes(rejectedFile.type)) {
											message += "Es sind nur PDF Dateien und Bilder möglich.";
										}

										if (rejectedFile.size > maxFileSize) {
											message +=
												"Die Datei ist zu groß. Es sind maximal  " +
												convertBytesToMbsOrKbs(maxFileSize) +
												" möglich.";
										}

										return message;
									}}
								/>
								{allowMultiple &&
									<Tooltip title="Lade mehrere Bild- oder PDF-Dateien hoch, die dann zu einer Datei kombiniert werden.">
										<Button
											variant="contained"
											size="small"
											className={classes.uploadActionButton}
											startIcon={<FileCopyOutlinedIcon />}
											onClick={() => setDropzoneDialogAction("multiple")}>
											mehrere Dateien laden
										</Button>
									</Tooltip>
								}
								{allowImages &&
									<Tooltip title="Lade mehrere Fotos hoch, die dann jeweils 4 pro Seite als PDF-Dokument gespeichert werden.">
										<Button
											variant="contained"
											size="small"
											className={classes.uploadActionButton}
											startIcon={<DashboardOutlinedIcon />}
											onClick={() => setDropzoneDialogAction("photos")}>
											Fotogalerie erstellen
										</Button>
									</Tooltip>
								}
							</>}
						<DropzoneDialog
							dropzoneClass={classes.dropzone}
							cancelButtonText={"Abbrechen"}
							submitButtonText={dropzoneDialogAction === "multiple" ? "Dokument erstellen" : "Dokument erweitern"}
							open={dropzoneDialogAction === "multiple" || dropzoneDialogAction === "prepend" || dropzoneDialogAction === "append"}
							onClose={() => setDropzoneDialogAction("")}
							onSave={(newFiles) => {
								uploadFiles(metafield, newFiles, dropzoneDialogAction);
								setDropzoneDialogAction("");
							}}
							acceptedFiles={[
								"application/pdf",
								"image/png",
								"image/jpeg",
								"image/gif",
							]}
							filesLimit={30}
							maxFileSize={20000000}
							showPreviewsInDropzone={false}
							showPreviews={true}
							useChipsForPreview={true}
							showFileNamesInPreview={true}
							showFileNames={false}
							showAlerts={['error', 'info']}
							dialogTitle="Dateien hochladen"
							dropzoneText={"Lade eine oder mehrere Bild- oder PDF-Dateien hoch, die dann zu einer Datei zusammengefügt werden"}
							getFileLimitExceedMessage={(filesLimit) => {
								if (filesLimit === 1)
									return "Bitte lade hier nur eine Datei hoch.";

								return "Bitte lade maximal " + filesLimit + " Dateien hoch.";
							}}
							previewText="Vorschau"
							getFileRemovedMessage={(fileName) => {
								return "Datei " + fileName + " entfernt.";
							}}
							getDropRejectMessage={(
								rejectedFile,
								acceptedFiles,
								maxFileSize
							) => {
								var message =
									"Die Datei " +
									rejectedFile.name +
									" kann nicht verwendet werden. ";

								if (!acceptedFiles.includes(rejectedFile.type)) {
									message += "Es sind nur PDF Dateien und Bilder möglich.";
								}

								if (rejectedFile.size > maxFileSize) {
									message +=
										"Die Datei ist zu groß. Es sind maximal  " +
										convertBytesToMbsOrKbs(maxFileSize) +
										" möglich.";
								}

								return message;
							}}
						/>
						<DropzoneDialog
							dropzoneClass={classes.dropzone}
							cancelButtonText={"Abbrechen"}
							submitButtonText={"Dokument erstellen"}
							open={dropzoneDialogAction === "photos"}
							onClose={() => setDropzoneDialogAction("")}
							onSave={(newFiles) => {
								uploadFiles(metafield, newFiles, dropzoneDialogAction);
								setDropzoneDialogAction("");
							}}
							acceptedFiles={[
								"image/png",
								"image/jpeg",
								"image/gif",
							]}
							filesLimit={30}
							maxFileSize={20000000}
							showPreviewsInDropzone={false}
							showPreviews={true}
							useChipsForPreview={false}
							showFileNamesInPreview={true}
							showFileNames={false}
							showAlerts={['error', 'info']}
							dialogTitle="Fotos hochladen"
							dropzoneText={"Bis zu 30 Fotos hier ablegen oder zum Auswählen klicken."}
							getFileLimitExceedMessage={(filesLimit) => {
								if (filesLimit === 1)
									return "Bitte lade hier nur eine Datei hoch.";

								return "Bitte lade maximal " + filesLimit + " Dateien hoch.";
							}}
							previewText="Vorschau"
							getFileRemovedMessage={(fileName) => {
								return "Datei " + fileName + " entfernt.";
							}}
							getDropRejectMessage={(
								rejectedFile,
								acceptedFiles,
								maxFileSize
							) => {
								var message =
									"Die Datei " +
									rejectedFile.name +
									" kann nicht verwendet werden. ";

								if (!acceptedFiles.includes(rejectedFile.type)) {
									message += "Es sind nur Bilder möglich.";
								}

								if (rejectedFile.size > maxFileSize) {
									message +=
										"Die Datei ist zu groß. Es sind maximal  " +
										convertBytesToMbsOrKbs(maxFileSize) +
										" möglich.";
								}

								return message;
							}}
						/>
					</Grid>
				</Grid>
			</Card>

		</Grid>);
}
